import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import {
  ArrowRightIcon,
  ArrowUpIcon,
  ListIcon,
  PlusIcon,
  TargetIcon,
  XIcon,
} from '@phosphor-icons/react';

import { CortexaLogo } from '@shared/ui/cortexa-logo';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@shared/ui/dialog';
import { Heading } from '@shared/ui/typography';

interface AIChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
  startNewChat?: boolean;
}

interface Message {
  role: 'user' | 'ai';
  content: string;
  variant?: 'topic' | 'question';
}

interface Chat {
  id: string;
  title: string;
  preview: string;
  date: string;
  messages: Message[];
}

const MOCK_CHATS: Chat[] = [
  {
    id: '1',
    title: 'September performance overview',
    preview: 'Can you analyze my September...',
    date: '2 days ago',
    messages: [
      { role: 'user', content: 'Can you analyze my September performance?' },
      {
        role: 'ai',
        content:
          'Of course! Based on your September data, you completed 45 practice questions with an 78% accuracy rate. Your strongest area was Data Insights (85% accuracy), while Verbal Reasoning needs some improvement (68% accuracy).',
      },
      { role: 'user', content: 'What should I focus on to improve?' },
      {
        role: 'ai',
        content:
          'I recommend focusing on Reading Comprehension and Critical Reasoning. Try spending 30 minutes daily on these topics and review your mistakes thoroughly.',
      },
    ],
  },
  {
    id: '2',
    title: 'GMAT Focus cheat codes down',
    preview: 'What are the best strategies...',
    date: '3 days ago',
    messages: [
      { role: 'user', content: 'What are the best strategies for GMAT Focus edition?' },
      {
        role: 'ai',
        content:
          'Here are the top strategies: 1) Master the new question types in Data Insights, 2) Practice time management - you have less time per question, 3) Focus on your weakest areas first, 4) Use the enhanced calculator wisely for Quant questions.',
      },
      { role: 'user', content: 'How is it different from the old GMAT?' },
      {
        role: 'ai',
        content:
          'GMAT Focus is shorter (2h 15min vs 3h 30min), has no essay section, includes a new Data Insights section, and allows you to choose the order of sections. The scoring is also different, ranging from 205-805.',
      },
    ],
  },
  {
    id: '3',
    title: 'Who secretly control the world',
    preview: 'I need help understanding...',
    date: '1 week ago',
    messages: [
      { role: 'user', content: 'I need help understanding global economics for the GMAT' },
      {
        role: 'ai',
        content:
          'For GMAT purposes, you should understand basic economic principles like supply and demand, market structures, and how central banks influence economies. These concepts often appear in Reading Comprehension and Critical Reasoning questions.',
      },
    ],
  },
  {
    id: '4',
    title: 'Quantitative strategies',
    preview: 'How can I improve my quant...',
    date: '1 week ago',
    messages: [
      { role: 'user', content: 'How can I improve my quantitative reasoning score?' },
      {
        role: 'ai',
        content:
          'Focus on these key areas: 1) Master fundamental concepts (algebra, geometry, arithmetic), 2) Learn to recognize problem patterns, 3) Practice mental math to save time, 4) Review your mistakes and understand why you got them wrong, 5) Time yourself to build speed.',
      },
      { role: 'user', content: 'What topics should I prioritize?' },
      {
        role: 'ai',
        content:
          'Prioritize: Word Problems (30% of questions), Algebra (25%), Geometry (20%), Number Properties (15%), and Statistics (10%). Start with your weakest area.',
      },
    ],
  },
  {
    id: '5',
    title: 'Verbal reasoning tips',
    preview: 'What are common mistakes in...',
    date: '2 weeks ago',
    messages: [
      { role: 'user', content: 'What are common mistakes in verbal reasoning?' },
      {
        role: 'ai',
        content:
          'Common mistakes include: 1) Not reading the entire passage carefully, 2) Bringing outside knowledge into answers, 3) Choosing answers that are partially correct, 4) Not identifying the question type first, 5) Spending too much time on difficult questions.',
      },
    ],
  },
  {
    id: '6',
    title: 'Study plan creation',
    preview: 'Help me create a 3-month...',
    date: '2 weeks ago',
    messages: [
      { role: 'user', content: 'Help me create a 3-month study plan for GMAT' },
      {
        role: 'ai',
        content:
          "Here's a 3-month plan:\n\nMonth 1: Build Foundation\n- Weeks 1-2: Learn all concepts (Quant & Verbal)\n- Weeks 3-4: Practice fundamentals, take diagnostic test\n\nMonth 2: Practice & Improve\n- Focus on weak areas\n- Take 2 practice tests\n- Review all mistakes thoroughly\n\nMonth 3: Test Prep\n- Take 4 full practice tests\n- Focus on timing and strategy\n- Final review of weak areas",
      },
      { role: 'user', content: 'How many hours per day should I study?' },
      {
        role: 'ai',
        content:
          'Aim for 2-3 hours on weekdays and 4-5 hours on weekends. Consistency is more important than cramming. Make sure to take one full day off per week to avoid burnout.',
      },
    ],
  },
];

const parseTopicMessage = (rawMessage: string): Message[] | null => {
  const lines = rawMessage.split('\n').map((line) => line.trim());
  const topicLine = lines.find((line) => line.startsWith('Topic:'));
  const questionLine = lines.find((line) => line.startsWith('Question:'));

  if (!topicLine || !questionLine) {
    return null;
  }

  const topic = topicLine.replace(/^Topic:\s*/i, '').trim();
  const question = questionLine.replace(/^Question:\s*/i, '').trim();

  if (!topic || !question) {
    return null;
  }

  return [
    { role: 'user', content: topic, variant: 'topic' },
    { role: 'user', content: question, variant: 'question' },
  ];
};

export function AIChatModal({
  isOpen,
  onClose,
  initialMessage,
  startNewChat = false,
}: AIChatModalProps) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [currentChatTitle, setCurrentChatTitle] = useState('New chat');

  const sendMessage = useCallback((messageToSend: string, shouldParseTopicMessage = false) => {
    if (!messageToSend.trim()) {
      return;
    }

    const parsedMessages = shouldParseTopicMessage ? parseTopicMessage(messageToSend) : null;
    const nextMessages = parsedMessages ?? [{ role: 'user' as const, content: messageToSend }];
    setMessages((previousMessages) => [...previousMessages, ...nextMessages]);
    setMessage('');

    setTimeout(() => {
      setMessages((previousMessages) => [
        ...previousMessages,
        {
          role: 'ai',
          content: 'This is a placeholder response from Cortexa AI. Integration coming soon!',
        },
      ]);
    }, 1000);
  }, []);

  const handleSend = useCallback(
    (customMessage?: string) => {
      const messageToSend = customMessage ?? message;
      sendMessage(messageToSend, Boolean(customMessage));
    },
    [message, sendMessage]
  );

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setMessage('');
    setCurrentChatId(null);
    setCurrentChatTitle('New chat');
  }, []);

  useEffect(() => {
    if (!isOpen || !initialMessage) {
      return;
    }

    if (startNewChat) {
      handleNewChat();
    }

    sendMessage(initialMessage, true);
  }, [handleNewChat, initialMessage, isOpen, sendMessage, startNewChat]);

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleOpenChat = (chat: Chat) => {
    setMessages(chat.messages);
    setCurrentChatId(chat.id);
    setCurrentChatTitle(chat.title);
    setMessage('');
  };

  const examplePrompts = [
    {
      icon: <TargetIcon size={25} weight="duotone" />,
      text: 'Prepare a summary of my performance.',
    },
    {
      icon: <TargetIcon size={25} weight="duotone" />,
      text: 'Explain Profit, loss, and discounts',
    },
    {
      icon: <TargetIcon size={25} weight="duotone" />,
      text: 'Create a personalized study plan',
    },
  ];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPortal>
        <DialogOverlay className="bg-black/40 duration-200" />
        <DialogPrimitive.Popup
          className="
            fixed top-1/2 left-1/2 z-50 flex h-[calc(100vh-2rem)] max-h-180 w-[calc(100vw-2rem)]
            max-w-7xl origin-bottom-left -translate-1/2 flex-col gap-0 overflow-hidden rounded-3xl
            bg-white p-0 text-sm shadow-2xl ring-0 duration-200 outline-none
            sm:max-w-7xl
            data-open:animate-in data-open:ease-out data-open:-zoom-in-30 data-open:fade-in
            data-closed:animate-out data-closed:ease-in data-closed:-zoom-out-75
            data-closed:fade-out
          "
        >
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => {
                setIsSidebarOpen((currentValue) => !currentValue);
              }}
              className="
                rounded-lg p-1.5 text-gray-500 transition-colors
                hover:bg-accent
              "
              type="button"
              aria-label="Toggle chat history"
            >
              <ListIcon className="size-5" />
            </button>
            <DialogTitle className="absolute left-1/2 -translate-x-1/2 text-base text-gray-900">
              {currentChatTitle}
            </DialogTitle>
            <button
              onClick={onClose}
              className="
                rounded-lg p-1.5 text-gray-500 transition-colors
                hover:bg-accent
              "
              type="button"
              aria-label="Close chat"
            >
              <XIcon className="size-5" />
            </button>
          </div>

          <div className="flex flex-1 flex-row overflow-hidden">
            <div
              className={`
                overflow-hidden transition-all duration-300 ease-in-out
                ${isSidebarOpen ? 'w-64 shrink-0' : 'w-0'}
              `}
            >
              <div className="flex h-full flex-col">
                <div className="p-3">
                  <h3 className="mb-2 text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Chats
                  </h3>
                  <button
                    onClick={handleNewChat}
                    className="
                      flex w-full items-center gap-2 rounded-lg bg-background-0 px-3 py-2.5 text-sm
                      font-medium text-gray-700 transition-colors
                      hover:bg-accent
                    "
                    type="button"
                  >
                    <PlusIcon className="size-4" />
                    New chat
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-2">
                  {MOCK_CHATS.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => {
                        handleOpenChat(chat);
                      }}
                      className={`
                        mb-1 w-full rounded-lg p-3 text-left transition-colors
                        hover:bg-accent
                        ${currentChatId === chat.id ? 'bg-accent' : ''}
                      `}
                      type="button"
                    >
                      <div className="flex flex-col gap-1">
                        <p className="truncate text-sm font-medium text-gray-900">{chat.title}</p>
                        <p className="truncate text-xs text-gray-500">{chat.preview}</p>
                        <p className="text-xs text-gray-400">{chat.date}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              {messages.length === 0 ? (
                <div
                  className="
                    m-1.5 flex flex-1 flex-col items-center justify-center rounded-2xl border
                    bg-accent px-6 pb-6
                  "
                >
                  <CortexaLogo
                    size="lg"
                    icon={true}
                    keepColors={false}
                    className="relative mt-1 mb-12 size-20 text-background-4 drop-shadow-recess"
                  />

                  <Heading tag="h3" size="xl" className="text-center">
                    Welcome Firstname, how can I help you today?
                  </Heading>

                  <div className="mt-6 w-full max-w-2xl min-w-0">
                    <div
                      className="
                        relative flex flex-col items-center gap-3 rounded-2xl border border-gray-200
                        bg-gray-50 px-4 py-3 shadow-sm transition-all
                        focus-within:border-gray-300 focus-within:bg-white focus-within:shadow-sm
                      "
                    >
                      <input
                        type="text"
                        value={message}
                        onChange={(event) => {
                          setMessage(event.target.value);
                        }}
                        onKeyDown={handleKeyPress}
                        placeholder="Ask Cortexa something..."
                        className="
                          w-full bg-transparent text-lg text-gray-900 outline-none
                          placeholder:text-gray-400
                        "
                      />
                      <div className="flex w-full flex-row justify-between">
                        <button
                          className="
                            shrink-0 text-gray-400 transition-colors
                            hover:text-gray-600
                          "
                          type="button"
                          aria-label="Add attachment"
                        >
                          <PlusIcon className="size-7" />
                        </button>
                        <button
                          onClick={() => {
                            handleSend();
                          }}
                          disabled={!message.trim()}
                          className="
                            flex size-10 shrink-0 items-center justify-center rounded-full
                            bg-blue-600 text-white transition-all
                            hover:bg-blue-700
                            disabled:cursor-not-allowed disabled:bg-gray-300
                          "
                          type="button"
                          aria-label="Send message"
                        >
                          <ArrowUpIcon className="size-6" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="mt-8 text-sm text-gray-500">Or try those examples to get started</p>

                  <div className="mt-2 flex max-w-[60vh] flex-wrap justify-center gap-3">
                    {examplePrompts.map((prompt) => (
                      <button
                        key={prompt.text}
                        onClick={() => {
                          handleSend(prompt.text);
                        }}
                        className="
                          flex items-center gap-2 rounded-full bg-background-4 px-4 py-2 text-sm
                          text-gray-700 shadow-sm transition-all
                          hover:border-gray-300 hover:shadow-sm
                        "
                        type="button"
                      >
                        <span>{prompt.icon}</span>
                        <span>{prompt.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="
                      mx-1.5 mt-1.5 flex flex-1 flex-col overflow-y-auto rounded-t-2xl border-x
                      border-t bg-accent px-6 pb-6
                    "
                  >
                    <div className="flex w-full flex-col gap-4 pt-4">
                      {messages.map((msg, index) => {
                        if (msg.variant === 'topic') {
                          return (
                            <div
                              key={`${msg.content}-${String(index)}`}
                              className="flex justify-end"
                            >
                              <div
                                className="
                                  flex items-center gap-2 rounded-full bg-background-4 px-4 py-2
                                  text-sm text-gray-700 shadow-sm
                                "
                              >
                                <ArrowRightIcon className="size-4" />
                                <span>{msg.content}</span>
                              </div>
                            </div>
                          );
                        }

                        if (msg.variant === 'question') {
                          return (
                            <div
                              key={`${msg.content}-${String(index)}`}
                              className="flex justify-end"
                            >
                              <div
                                className="
                                  max-w-[80%] rounded-2xl bg-white px-6 py-4 text-gray-900 shadow-sm
                                "
                              >
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={`${msg.content}-${String(index)}`}
                            className={`
                              flex
                              ${msg.role === 'user' ? 'justify-end' : 'justify-start'}
                            `}
                          >
                            <div
                              className={`
                                max-w-[80%] rounded-2xl px-4 py-3 shadow-sm
                                ${
                                  msg.role === 'user'
                                    ? 'bg-card text-gray-900'
                                    : 'bg-gray-100 text-gray-900'
                                }
                              `}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div
                    className="
                      mx-1.5 mb-1.5 rounded-b-2xl border-x border-b bg-accent px-6 pt-4 pb-6
                    "
                  >
                    <div
                      className="
                        relative flex flex-col items-center gap-3 rounded-2xl border border-gray-200
                        bg-gray-50 px-4 py-3 shadow-sm transition-all
                        focus-within:border-gray-300 focus-within:bg-white focus-within:shadow-sm
                      "
                    >
                      <input
                        type="text"
                        value={message}
                        onChange={(event) => {
                          setMessage(event.target.value);
                        }}
                        onKeyDown={handleKeyPress}
                        placeholder="Ask Cortexa something..."
                        className="
                          w-full bg-transparent text-lg text-gray-900 outline-none
                          placeholder:text-gray-400
                        "
                      />
                      <div className="flex w-full flex-row justify-between">
                        <button
                          className="
                            shrink-0 text-gray-400 transition-colors
                            hover:text-gray-600
                          "
                          type="button"
                          aria-label="Add attachment"
                        >
                          <PlusIcon className="size-7" />
                        </button>
                        <button
                          onClick={() => {
                            handleSend();
                          }}
                          disabled={!message.trim()}
                          className="
                            flex size-10 shrink-0 items-center justify-center rounded-full
                            bg-blue-600 text-white transition-all
                            hover:bg-blue-700
                            disabled:cursor-not-allowed disabled:bg-gray-300
                          "
                          type="button"
                          aria-label="Send message"
                        >
                          <ArrowUpIcon className="size-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogPrimitive.Popup>
      </DialogPortal>
    </Dialog>
  );
}
